import { Box, Button } from '@mui/material'

import { useAutoScroll } from '@/atoms'
import { LayoutComposer, ListFooter, NoEntries, NoResults } from '@/components/List'
import { ListLoading } from '@/components/ListLoading'
import { StructuredVirtualList } from '@/components/StructuredVirtualList'
import { ComponentSize, FormColumn, ListOverlayEditCover } from '@/components/layout'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'

import { Item } from './Item'
import { Header } from './components/Header'

import { useDataForList } from './hooks/useDataForList'
import { useListHandlers } from './hooks/useListHandlers'
import { useQueryParameterSelection } from './hooks/useQueryParameterSelection'
import { useTexts } from './hooks/useTexts'

interface Props {
  vehicles: Record<string, uui.domain.client.UnifiedVehicle>
  selection: workwave.DeepReadonly<string[]>
  editing: boolean
}

// Return the height of the item
const getItemHeight = () => 78

// Return the id of the item
const getItemId = (item: uui.domain.client.UnifiedVehicle) => item.unifiedId

export function List(props: Props) {
  const { vehicles, selection, editing } = props

  const texts = useTexts()

  const { handleOnSelect, handleOnDoubleClick, handleOnSelectAll } = useListHandlers(selection)
  const { structure, setCreate, isLoading, lastSelectedVehicle } = useDataForList()

  // This is a hook that will scroll the list to the last selected item
  useAutoScroll('unifiedVehicles', lastSelectedVehicle)

  // This hook will handle the selection of an item if provided via query parameter
  useQueryParameterSelection()

  const allSelected = selection.length >= structure.shownItems
  const multiSelect = selection.length > 1

  return (
    <FormColumn width={320} testid="form__column-primary">
      {isLoading ? (
        <ListLoading />
      ) : (
        <>
          <Header />

          <Box width="100%" height="100%" flex="0 1 auto" position="relative">
            <LayoutComposer
              totalItems={structure.totalItems}
              shownItems={structure.shownItems}
              noResults={
                <NoResults
                  title={texts.noResults.title}
                  description={texts.noResults.description}
                />
              }
              noEntries={
                <NoEntries
                  title={texts.noEntries.title}
                  description={texts.noEntries.description}
                  action={
                    <ReadOnlyTooltip
                      render={preventEditing => (
                        <Button
                          size="large"
                          variant="outlined"
                          disabled={preventEditing || editing}
                          onClick={setCreate}
                          data-testid="no-entries-add-item"
                          data-trackid="no-entries-add-item"
                        >
                          {texts.noEntries.action}
                        </Button>
                      )}
                    />
                  }
                />
              }
              list={
                <ComponentSize
                  render={size => {
                    return (
                      <>
                        <StructuredVirtualList<'unifiedVehicles'>
                          buffer={50}
                          onSelect={handleOnSelect}
                          getItemId={getItemId}
                          itemsById={vehicles}
                          structure={structure}
                          RenderItem={Item}
                          listHeight={size.height}
                          onDoubleClick={handleOnDoubleClick}
                          getItemHeights={getItemHeight}
                          selectedItemIds={selection}
                          listApiCategory="unifiedVehicles"
                        />
                        {editing && <ListOverlayEditCover />}
                      </>
                    )
                  }}
                />
              }
            />
          </Box>

          {multiSelect && (
            <ListFooter
              label={texts.selectBtn(allSelected)}
              onToggleSelection={handleOnSelectAll}
              checked={allSelected}
            />
          )}
        </>
      )}
    </FormColumn>
  )
}
